import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { isEmpty } from 'lodash'
import { Link } from 'react-router-dom'
import { Panel } from 'primereact/panel'

import Text from '../../../../ui/Text'
import { SFormRow, SLabelText } from '../../../styles/Form'
import { Company, LoadBalancerGroup, useLoadBalancerGroupUpdateMutation } from '../../../redux/api/web-api'
import { LBgroupDescriptionValidation } from '../load-balancer-group-form-validation'
import { Combobox } from '../../../components/combobox'
import { SActions } from '../../chargepoint-details-widget/chargepoint-details-widget.styles'
import { notifySuccess, notifyError } from '../../notificationsManager/Notify'
import { Button } from '../../../components/Button'
import Input from '../../../components/Input'
import { SDisabledText, SRedirectLink } from '../../../../ui/loadBalancer/LoadBalancer.styles'
import useText from '../../../hooks/useText'

interface LoadBalancerDetailsInputsProps {
    loadBalancerGroup: LoadBalancerGroup
    companies: Company[]
    hasChargers: boolean
}

export const LoadBalancerDetailsInputs = ({
    loadBalancerGroup,
    companies,
    hasChargers,
}: LoadBalancerDetailsInputsProps): React.ReactElement => {
    const text = useText()

    const schema = yup.object().shape({
        description: LBgroupDescriptionValidation,
    })

    const [company, setCompany] = useState(loadBalancerGroup?.companyId)
    const {
        register,
        handleSubmit,
        watch,
        reset,
        control,
        formState: { errors, isValid, isDirty, isSubmitSuccessful },
    } = useForm<LoadBalancerGroup>({
        resolver: yupResolver(schema, { abortEarly: false }),
        mode: 'all',
    })

    const [updateLoadBalancerGroup, { isLoading }] = useLoadBalancerGroupUpdateMutation()

    const changeCompany = watch('companyId')

    const saveLbgroup = async (formData: LoadBalancerGroup) => {
        let tempLoadBalancerGroup = {
            ...loadBalancerGroup,
            description: formData?.description,
            companyId: loadBalancerGroup ? company : formData?.companyId,
        }
        if (!isEmpty(changeCompany) && loadBalancerGroup?.measurementDeviceId) {
            tempLoadBalancerGroup = {
                ...loadBalancerGroup,
                description: formData?.description,
                companyId: loadBalancerGroup ? company : formData?.companyId,
                measurementDeviceId: null,
            }
        }
        try {
            await updateLoadBalancerGroup({
                loadBalancerGroup: tempLoadBalancerGroup,
            }).unwrap()
            notifySuccess(text('msgLoadbalancerGroupUpdate'))
        } catch (error) {
            notifyError(text('msgLoadbalancerGroupUpdateError'))
        }
    }

    useEffect(() => {
        setCompany(loadBalancerGroup?.companyId)
    }, [loadBalancerGroup?.companyId, setCompany])

    useEffect(() => {
        if (isSubmitSuccessful) {
            reset({}, { keepValues: true })
        }
    }, [isSubmitSuccessful, reset])

    return (
        <Panel toggleable header={text('lblLBInformation')} data-testid="loadbalancer-info">
            <div className="ml-4 mr-4">
                <SLabelText required={true} data-testid="loadbalancer-org-label">
                    <Text code="lblOrganisation" />
                </SLabelText>
                <SFormRow>
                    <div data-testid="company-redirect" className="flex justify-content-between ">
                        <span className="w-9 xl:w-10">
                            <Controller
                                name="companyId"
                                control={control}
                                render={({ field: { onChange } }) => (
                                    <Combobox
                                        id="organisation"
                                        required
                                        enableDefaultOption={false}
                                        options={companies?.map((company) => ({
                                            value: company?.id,
                                            label: company?.name || '',
                                        }))}
                                        value={company}
                                        {...register('companyId')}
                                        onChange={(option) => {
                                            setCompany(option?.value)
                                            onChange(option?.value)
                                        }}
                                        disabled={hasChargers}
                                        data-testid="company-input"
                                        disabledReason={hasChargers ? 'lblLoadBalancerHasChargePoints' : undefined}
                                    />
                                )}
                            />
                        </span>
                        <span className="mt-3">
                            {isDirty ? (
                                <SDisabledText data-testid="go-to-company-disabled">
                                    <Text code="lblGoTo" />
                                </SDisabledText>
                            ) : (
                                <SRedirectLink
                                    as={Link}
                                    to={`/company/${company}`}
                                    data-testid="go-to-company"
                                    title={text('btnGoToOrganisation')}
                                >
                                    <Text code="lblGoTo" />
                                </SRedirectLink>
                            )}
                        </span>
                    </div>
                </SFormRow>
                <SLabelText data-testid="loadbalancer-description-label">
                    <Text code="lblDescription" />
                </SLabelText>

                <SFormRow>
                    <Input
                        type="text"
                        id="description"
                        data-testid="description-input"
                        {...register('description')}
                        error={errors.description?.message}
                        defaultValue={loadBalancerGroup?.description ?? undefined}
                    />
                </SFormRow>

                <SActions>
                    <Button
                        label={text('btnSave')}
                        data-testid="save-button"
                        onClick={handleSubmit(saveLbgroup)}
                        disabled={isLoading || !isValid || !isDirty}
                        size="sm"
                        loading={isLoading}
                    />
                </SActions>
            </div>
        </Panel>
    )
}

export default LoadBalancerDetailsInputs
